import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Modal from '../components/Modal';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { useHeader } from '@/contexts/HeaderContext';
import Loading from '../components/Loading';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── helpers ─────────────────────────────────────── */
const EVENT_ICONS = {
  anniversary: 'solar:heart-linear',
  birthday: 'solar:cake-linear',
  vacation: 'solar:sun-2-linear',
  custom: 'solar:gift-linear',
};

const EVENT_DOT_COLORS = {
  anniversary: '#ec4899',
  birthday: '#f59e0b',
  vacation: '#3b82f6',
  custom: '#191919',
};

const MONTH_NAMES_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_NAMES_IT = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const MONTH_NAMES_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const WEEKDAYS_EN = ['Mo','Tu','We','Th','Fr','Sa','Su'];
const WEEKDAYS_IT = ['Lu','Ma','Me','Gi','Ve','Sa','Do'];
const WEEKDAYS_ES = ['Lu','Ma','Mi','Ju','Vi','Sá','Do'];

const toDateKey = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatEventDate = (dateString, lang) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(lang?.startsWith('it') ? 'it-IT' : lang?.startsWith('es') ? 'es-ES' : 'en-US', {
    day: 'numeric',
    month: 'long',
  }).format(d).toUpperCase();
};

const getDaysLabel = (daysUntil, t, lang) => {
  if (daysUntil == null) return '';
  if (daysUntil === 0) return t('events.today') || (lang?.startsWith('it') ? 'Oggi' : lang?.startsWith('es') ? 'Hoy' : 'Today');
  return lang?.startsWith('it') ? `tra ${daysUntil} giorni` : lang?.startsWith('es') ? `en ${daysUntil} días` : `in ${daysUntil} days`;
};

/* ════════════════════════════════════════════════════
   Events — Calendar view
   ════════════════════════════════════════════════════ */
const Events = () => {
  const { t, i18n } = useTranslation();
  const { user: contextUser, isPremium: contextIsPremium, loading: userLoading } = useUser();
  const { setHeaderProps } = useHeader();
  const userId = contextUser?.user_id || contextUser?.id;
  const isIt = i18n.language?.startsWith('it');
  const isEs = i18n.language?.startsWith('es');

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [formData, setFormData] = useState({
    event_type: 'anniversary',
    title: '',
    date: '',
    recurring: true,
    recurrence: 'yearly',
    notes: '',
  });

  // Calendar state
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(toDateKey(new Date()));

  const resumeListenerRef = useRef(null);
  const visibilityListenerRef = useRef(null);

  const monthNames = isIt ? MONTH_NAMES_IT : isEs ? MONTH_NAMES_ES : MONTH_NAMES_EN;
  const weekdays = isIt ? WEEKDAYS_IT : isEs ? WEEKDAYS_ES : WEEKDAYS_EN;

  /* ── API ────────────────────────────────────────── */
  const fetchEvents = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/events/${userId}`);
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('[Events] fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        event_type: formData.event_type,
        title: formData.title,
        date: formData.date,
        recurring: !!formData.recurring,
        notes: formData.notes,
        recurrence: formData.recurrence,
      };

      if (editingEventId) {
        await axios.put(`${API}/events/${editingEventId}`, payload);
        toast.success(t('events.eventUpdated') || (isIt ? 'Evento aggiornato' : isEs ? 'Evento actualizado' : 'Event updated'));
      } else {
        await axios.post(`${API}/events`, { ...payload, user_id: userId });
        toast.success(t('events.eventAdded'));
      }

      setShowForm(false);
      setEditingEventId(null);
      setFormData({ event_type: 'anniversary', title: '', date: '', recurring: true, recurrence: 'yearly', notes: '' });
      fetchEvents();
    } catch (err) {
      console.error('[Events] save error', err);
      const detail = err.response?.data?.detail || err.message;
      toast.error(isIt ? `Errore nel salvataggio: ${detail}` : isEs ? `Error al guardar: ${detail}` : `Error saving event: ${detail}`);
    }
  };

  const handleDelete = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}`);
      setEvents((prev) => prev.filter((e) => e.event_id !== eventId));
      if (selectedEvent?.event_id === eventId) setSelectedEvent(null);
      toast.success(t('events.eventDeleted'));
    } catch (err) {
      console.error('[Events] delete error', err);
      toast.error(isIt ? 'Errore nella cancellazione' : isEs ? 'Error al eliminar el evento' : 'Error deleting event');
    }
  };

  /* ── Effects ────────────────────────────────────── */
  useEffect(() => {
    if (userLoading || !userId) return;
    // Respect localStorage preference (source of truth) over server profile
    const savedLang = localStorage.getItem('betterhusbandUserLang');
    const lang = savedLang || contextUser?.language;
    if (lang && i18n.language !== lang) i18n.changeLanguage(lang);
    fetchEvents();

    const onResume = () => fetchEvents();
    resumeListenerRef.current = onResume;
    visibilityListenerRef.current = () => {
      if (document.visibilityState === 'visible') onResume();
    };

    CapacitorApp.addListener('resume', resumeListenerRef.current);
    window.addEventListener('visibilitychange', visibilityListenerRef.current);

    return () => {
      CapacitorApp.removeAllListeners();
      window.removeEventListener('visibilitychange', visibilityListenerRef.current);
    };
  }, [userLoading, userId, contextUser, contextIsPremium, i18n, fetchEvents]);

  useEffect(() => {
    setHeaderProps({
      title: t('events.title'),
      user: contextUser,
      rightElement: (
        <button
          data-testid="add-event-btn"
          onClick={() => {
            setFormData(prev => ({ ...prev, date: selectedDate }));
            setShowForm(true);
          }}
          className="w-6 h-6 rounded-full bg-stone-900 dark:bg-[#EFEFEF] text-white dark:text-[#18181C] flex items-center justify-center hover:bg-stone-800 dark:hover:bg-white transition-all active:scale-95"
        >
          <Icon icon="solar:add-circle-linear" width={14} />
        </button>
      ),
    });
  }, [t, contextUser, setHeaderProps, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Calendar logic ─────────────────────────────── */
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      if (!ev.date) return;
      const evDate = new Date(ev.date);
      if (isNaN(evDate.getTime())) return;

      if (ev.recurring && ev.recurrence === 'yearly') {
        const key = `${viewYear}-${String(evDate.getMonth() + 1).padStart(2, '0')}-${String(evDate.getDate()).padStart(2, '0')}`;
        if (!map[key]) map[key] = [];
        map[key].push(ev);
      } else {
        const key = toDateKey(evDate);
        if (!map[key]) map[key] = [];
        map[key].push(ev);
      }
    });
    return map;
  }, [events, viewYear]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0);
    let startOffset = firstDay.getDay() - 1;
    if (startOffset < 0) startOffset = 6;

    const days = [];
    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    return days;
  }, [viewYear, viewMonth]);

  const goMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setViewMonth(m);
    setViewYear(y);
  };

  const todayKey = toDateKey(today);

  const eventsForSelected = useMemo(() => {
    return eventsByDate[selectedDate] || [];
  }, [eventsByDate, selectedDate]);

  /* ── Internal: Event Form ───────────────────────── */
  const renderEventForm = (noBg = false) => (
    <form
      onSubmit={handleSubmit}
      style={{ background: noBg ? 'inherit' : 'var(--c-surface)', fontFamily: 'Manrope-Variable', fontStyle: 'normal' }}
      className="space-y-3"
    >
      <div>
        <label className="block text-xs text-stone-400 dark:text-[#606060] mb-1.5" style={{ fontFamily: 'Manrope-Variable' }}>{t('events.eventType')}</label>
        <select value={formData.event_type} onChange={(e) => setFormData({ ...formData, event_type: e.target.value })} className="w-full h-9 rounded-full border border-stone-200 dark:border-[#2E2E36] px-2 bg-inherit text-stone-900 dark:text-[#EFEFEF] outline-none" style={{ fontFamily: 'Manrope-Variable' }}>
          <option value="anniversary">{t('events.anniversary')}</option>
          <option value="birthday">{t('events.birthday')}</option>
          <option value="vacation">{t('events.vacation')}</option>
          <option value="custom">{t('events.custom')}</option>
        </select>
      </div>

      <div>
        <label className="block text-xs text-stone-400 dark:text-[#606060] mb-1.5" style={{ fontFamily: 'Manrope-Variable' }}>{t('events.eventTitle')}</label>
        <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="w-full h-9 rounded-full border border-stone-200 dark:border-[#2E2E36] px-2 bg-inherit text-stone-900 dark:text-[#EFEFEF] outline-none" style={{ fontFamily: 'Manrope-Variable' }} />
      </div>

      <div>
        <label className="block text-xs text-stone-400 dark:text-[#606060] mb-1.5" style={{ fontFamily: 'Manrope-Variable' }}>{t('events.date')}</label>
        <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="w-full h-9 rounded-full border border-stone-200 dark:border-[#2E2E36] px-2 bg-inherit text-stone-900 dark:text-[#EFEFEF] outline-none" style={{ fontFamily: 'Manrope-Variable' }} />
      </div>

      <div>
        <div className="flex justify-between items-center">
          <div>
            <label className="block text-xs text-stone-400 dark:text-[#606060] mb-1" style={{ fontFamily: 'Manrope-Variable' }}>{t('events.recurring')}</label>
            <div className="text-[11px] text-stone-400 dark:text-[#606060]">{isIt ? 'Ripeti questo evento' : isEs ? 'Repetir este evento' : 'Repeat this event'}</div>
          </div>
          <button
            onClick={() => setFormData({ ...formData, recurring: !formData.recurring, recurrence: !formData.recurring && formData.recurrence === 'none' ? 'yearly' : formData.recurrence })}
            type="button"
            aria-pressed={!!formData.recurring}
            style={{ width: 44, height: 24, borderRadius: 12, background: formData.recurring ? 'var(--c-texPri)' : 'var(--c-surface-elevated)', border: 'none', padding: 4, display: 'flex', alignItems: 'center', overflow: 'hidden' }}
          >
            <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--c-surface)', transform: formData.recurring ? 'translateX(20px)' : 'translateX(0)', transition: 'transform 160ms ease' }} />
          </button>
        </div>

        {formData.recurring && (
          <div className="mt-2.5">
            <select value={formData.recurrence} onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })} className="w-full h-9 rounded-full border border-stone-200 dark:border-[#2E2E36] px-2 bg-inherit text-stone-900 dark:text-[#EFEFEF] outline-none" style={{ fontFamily: 'Manrope-Variable' }}>
              <option value="daily">{t('events.recurrence_daily')}</option>
              <option value="weekly">{t('events.recurrence_weekly')}</option>
              <option value="monthly">{t('events.recurrence_monthly')}</option>
              <option value="yearly">{t('events.recurrence_yearly')}</option>
            </select>
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs text-stone-400 dark:text-[#606060] mb-1.5" style={{ fontFamily: 'Manrope-Variable' }}>{t('events.notes')}</label>
        <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full min-h-[80px] border border-stone-200 dark:border-[#2E2E36] p-2 bg-inherit text-stone-900 dark:text-[#EFEFEF] outline-none resize-none" style={{ fontFamily: 'Manrope-Variable', borderRadius: 6 }} />
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-stone-100 dark:border-[#2E2E36]">
        <button type="button" onClick={() => { setShowForm(false); setEditingEventId(null); }} className="px-4 py-2 text-stone-500 dark:text-[#606060] hover:text-stone-700 dark:hover:text-[#EFEFEF] transition-colors uppercase text-sm">
          {t('events.cancel')}
        </button>
        <button type="submit" className="px-6 py-2 bg-stone-900 text-white hover:bg-stone-800 transition-colors font-medium uppercase text-sm">
          {t('events.save')}
        </button>
      </div>
    </form>
  );

  /* ── Event detail view ──────────────────────────── */
  const EventDetail = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-6 pt-2 pb-10">
      <div className="flex items-center justify-between mb-8">
        <button onClick={() => setSelectedEvent(null)} className="flex items-center gap-1 text-stone-500 dark:text-[#606060] hover:text-stone-700 dark:hover:text-[#EFEFEF] transition-colors text-sm">
          <Icon icon="solar:alt-arrow-left-linear" width={18} />
          {t('events.back') || (isIt ? 'Indietro' : isEs ? 'Atrás' : 'Back')}
        </button>
        <div className="flex items-center gap-2">
          <button
            data-testid={`edit-event-${selectedEvent.event_id}-btn`}
            onClick={() => {
              setFormData({
                event_type: selectedEvent.event_type || 'anniversary',
                title: selectedEvent.title || '',
                date: selectedEvent.date || '',
                recurring: selectedEvent.recurring ?? true,
                recurrence: selectedEvent.recurrence || (selectedEvent.recurring ? 'yearly' : 'none'),
                notes: selectedEvent.notes || '',
              });
              setEditingEventId(selectedEvent.event_id);
              setShowForm(true);
            }}
            className="w-9 h-9 rounded-full bg-stone-100 dark:bg-[#222228] hover:bg-stone-200 dark:hover:bg-[#2E2E36] flex items-center justify-center transition-colors"
          >
            <Icon icon="solar:pen-linear" width={16} className="text-stone-600 dark:text-[#909090]" />
          </button>
          <button
            data-testid={`delete-event-${selectedEvent.event_id}-btn`}
            onClick={() => handleDelete(selectedEvent.event_id)}
            className="w-9 h-9 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
          >
            <Icon icon="solar:trash-bin-minimalistic-linear" width={16} className="text-red-500" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Icon icon={EVENT_ICONS[selectedEvent.event_type] || EVENT_ICONS.custom} width={14} className="text-stone-400 dark:text-[#606060]" />
        <span className="text-[10px] tracking-[0.18em] uppercase text-stone-400 dark:text-[#606060]" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace' }}>
          {formatEventDate(selectedEvent.date, i18n.language)}
          {selectedEvent.days_until != null && ` · ${getDaysLabel(selectedEvent.days_until, t, i18n.language)}`}
        </span>
      </div>

      <h1 className="text-2xl font-medium text-stone-900 dark:text-[#EFEFEF] mb-4" style={{ fontFamily: 'Manrope-Variable' }}>
        {selectedEvent.title}
      </h1>

      {selectedEvent.recurring && (
        <div className="flex items-center gap-1.5 mb-4">
          <Icon icon="solar:restart-linear" width={13} className="text-stone-400 dark:text-[#606060]" />
          <span className="text-[11px] text-stone-400 dark:text-[#606060] uppercase tracking-wider" style={{ fontFamily: 'Manrope-Variable' }}>
            {t(`events.recurrence_${selectedEvent.recurrence || 'yearly'}`)}
          </span>
        </div>
      )}

      <p className="text-stone-700 dark:text-[#EFEFEF] whitespace-pre-wrap leading-relaxed italic" style={{ fontFamily: 'Manrope-Variable', fontSize: 15 }}>
        {selectedEvent.notes || (isIt ? 'Nessuna nota.' : isEs ? 'Sin notas.' : 'No notes.')}
      </p>
    </motion.div>
  );

  /* ── Render ─────────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center page-container" style={{ background: 'inherit' }}>
        <Loading size="hundred" message={null} />
      </div>
    );
  }

  return (
    <div style={{ background: 'inherit' }}>
      <AnimatePresence mode="wait">
        {/* ── Detail view ──────────────────────────── */}
        {selectedEvent && !showForm && <EventDetail key="detail" />}

        {/* ── Edit modal ───────────────────────────── */}
        {showForm && editingEventId && (
          <Modal key="edit" placement="bottom" onClose={() => { setShowForm(false); setEditingEventId(null); }} ariaLabel={isIt ? 'Modifica evento' : isEs ? 'Editar evento' : 'Edit event'}>
            <motion.div initial={{ scale: 0.98, opacity: 0, y: 8 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.98, opacity: 0, y: 8 }} transition={{ duration: 0.16 }} className="w-[min(92%,520px)] max-w-md z-50">
              <div style={{ width: 46, height: 4, background: 'var(--c-border)', borderRadius: 999, margin: '6px auto' }} />
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--c-texSec)', textTransform: 'uppercase', letterSpacing: '0.26em', marginBottom: 32 }}>{isIt ? 'Modifica evento' : isEs ? 'Editar evento' : 'Edit event'}</div>
              {renderEventForm(true)}
            </motion.div>
          </Modal>
        )}

        {/* ── New event modal ──────────────────────── */}
        {showForm && !editingEventId && (
          <Modal key="new" placement="bottom" onClose={() => { setShowForm(false); setEditingEventId(null); }} ariaLabel={isIt ? 'Nuovo evento' : isEs ? 'Nuevo evento' : 'New event'}>
            <motion.div initial={{ scale: 0.98, opacity: 0, y: 8 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.98, opacity: 0, y: 8 }} transition={{ duration: 0.16 }} className="w-[min(100%,520px)] max-w-md z-50 px-0">
              <div style={{ width: 46, height: 4, background: 'var(--c-border)', borderRadius: 999, margin: '6px auto' }} />
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--c-texSec)', textTransform: 'uppercase', letterSpacing: '0.26em', marginBottom: 32 }}>{isIt ? 'Nuovo evento' : isEs ? 'Nuevo evento' : 'New event'}</div>
              {renderEventForm(true)}
            </motion.div>
          </Modal>
        )}

        {/* ── Calendar view ────────────────────────── */}
        {!selectedEvent && !showForm && (
          <motion.div key="calendar" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div style={{ maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', padding: '0 24px 40px' }}>

              {/* Month nav */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 0 16px' }}>
                <button onClick={() => goMonth(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
                  <Icon icon="solar:alt-arrow-left-linear" style={{ width: 20, height: 20, color: 'var(--c-texSec)' }} />
                </button>
                <div style={{ fontFamily: 'Manrope-Variable', fontSize: 16, fontWeight: 600, color: 'var(--c-texPri)' }}>
                  {monthNames[viewMonth]} {viewYear}
                </div>
                <button onClick={() => goMonth(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
                  <Icon icon="solar:alt-arrow-right-linear" style={{ width: 20, height: 20, color: 'var(--c-texSec)' }} />
                </button>
              </div>

              {/* Weekday headers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, marginBottom: 4 }}>
                {weekdays.map((d) => (
                  <div key={d} style={{
                    textAlign: 'center', fontFamily: 'Manrope-Variable', fontSize: 11, fontWeight: 500,
                    color: 'var(--c-texSec)', padding: '4px 0', textTransform: 'uppercase',
                  }}>
                    {d}
                  </div>
                ))}
              </div>

              {/* Day grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0 }}>
                {calendarDays.map((day, idx) => {
                  if (day === null) return <div key={`pad-${idx}`} />;
                  const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isToday = dateKey === todayKey;
                  const isSelected = dateKey === selectedDate;
                  const dayEvents = eventsByDate[dateKey] || [];
                  const hasEvents = dayEvents.length > 0;

                  return (
                    <button
                      key={dateKey}
                      onClick={() => setSelectedDate(dateKey)}
                      style={{
                        background: isSelected ? 'var(--c-texPri)' : 'transparent',
                        border: 'none', cursor: 'pointer', padding: 0,
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: 3,
                        height: 48, borderRadius: 12, transition: 'background 0.15s',
                      }}
                    >
                      <span style={{
                        fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: isToday ? 700 : 400,
                        color: isSelected ? 'var(--c-bg, #fff)' : 'var(--c-texPri)',
                        lineHeight: 1,
                      }}>
                        {day}
                      </span>
                      <div style={{ display: 'flex', gap: 3, height: 5 }}>
                        {hasEvents ? dayEvents.slice(0, 3).map((ev, i) => (
                          <div key={i} style={{
                            width: 5, height: 5, borderRadius: '50%',
                            background: isSelected ? 'var(--c-bg, #fff)' : (EVENT_DOT_COLORS[ev.event_type] || '#191919'),
                          }} />
                        )) : null}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Events for selected date */}
              <div style={{ marginTop: 24 }}>
                <div style={{
                  fontFamily: 'Manrope-Variable', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.1em',
                  color: 'var(--c-texSec)', marginBottom: 12,
                }}>
                  {formatEventDate(selectedDate, i18n.language) || selectedDate}
                </div>

                {eventsForSelected.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {eventsForSelected.map((ev) => (
                      <button
                        key={ev.event_id}
                        onClick={() => setSelectedEvent(ev)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 14,
                          padding: '14px 16px', borderRadius: 16, border: 'none', cursor: 'pointer',
                          background: 'var(--c-bg, #fff)', width: '100%', textAlign: 'left',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                        }}
                      >
                        <div style={{
                          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'var(--c-bg-secondary, #F5F0EB)',
                        }}>
                          <Icon icon={EVENT_ICONS[ev.event_type] || EVENT_ICONS.custom}
                            style={{ width: 20, height: 20, color: EVENT_DOT_COLORS[ev.event_type] || '#191919' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 600, color: 'var(--c-texPri)' }}>
                            {ev.title}
                          </div>
                          {ev.notes && (
                            <div style={{
                              fontFamily: 'Manrope-Variable', fontSize: 12, color: 'var(--c-texSec)',
                              marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {ev.notes}
                            </div>
                          )}
                        </div>
                        <Icon icon="solar:alt-arrow-right-linear" style={{ width: 16, height: 16, color: 'var(--c-texSec)', flexShrink: 0 }} />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    fontFamily: 'Manrope-Variable', fontSize: 13, color: 'var(--c-texSec)',
                    fontStyle: 'italic', textAlign: 'center', padding: '20px 0',
                  }}>
                    {isIt ? 'Nessun evento in questo giorno' : isEs ? 'Sin eventos en este día' : 'No events on this day'}
                  </div>
                )}
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Events;